import { CurrencyFormatter } from './currency-formatter';

export function normalizeSaleValue(value: number | string | null | undefined): number {
  return CurrencyFormatter.formatForBackend(value ?? '');
}

export function isPositiveSaleValue(value: number | string | null | undefined): boolean {
  return normalizeSaleValue(value) > 0;
}

export function getSaleValueValidationError(
  value: number | string | null | undefined,
  sparePartLabel?: string
): string | null {
  if (isPositiveSaleValue(value)) {
    return null;
  }

  const prefix = sparePartLabel ? `Repuesto "${sparePartLabel}"` : 'El repuesto';
  return `${prefix}: el valor de venta debe ser mayor a 0`;
}

export function validateSparePartsSaleValues(
  spareParts: Array<{ valorDeVenta?: number | string | null; sparePartName?: string }>
): string | null {
  for (let index = 0; index < spareParts.length; index++) {
    const part = spareParts[index];
    const label = part.sparePartName?.trim() || `#${index + 1}`;
    const error = getSaleValueValidationError(part.valorDeVenta, label);
    if (error) {
      return error;
    }
  }

  return null;
}

export const LOW_SALE_PRICE_RATIO_THRESHOLD = 11;

export interface LowSaleRatioSparePartCandidate {
  unitPrice?: number | string | null;
  valorDeVenta?: number | string | null;
  sparePartName?: string | null;
  productCode?: string | null;
  orderNumber?: string | null;
  siniestro?: string | null;
}

export interface LowSaleRatioViolation {
  index: number;
  ratio: number;
  label: string;
}

export function getSaleToUnitRatio(
  value: Pick<LowSaleRatioSparePartCandidate, 'valorDeVenta' | 'unitPrice'>
): number | null {
  const unitPrice = normalizeSaleValue(value.unitPrice ?? null);
  if (unitPrice < 0) {
    return null;
  }
  if (unitPrice === 0) {
    return 0;
  }

  const saleValue = normalizeSaleValue(value.valorDeVenta ?? null);
  return saleValue / unitPrice;
}

export function isLowSaleToUnitRatio(
  value: Pick<LowSaleRatioSparePartCandidate, 'valorDeVenta' | 'unitPrice'>,
  threshold: number = LOW_SALE_PRICE_RATIO_THRESHOLD
): boolean {
  const ratio = getSaleToUnitRatio(value);
  return ratio !== null && ratio <= threshold;
}

export function formatLowSaleRatioIdentifier(
  part: LowSaleRatioSparePartCandidate,
  index: number
): string {
  const sparePartName = (part.sparePartName || '').toString().trim() || '-';
  const productCode = (part.productCode || '').toString().trim() || '-';
  const orderNumber = (part.orderNumber || '').toString().trim() || '-';
  const siniestro = (part.siniestro || '').toString().trim() || '-';
  return `#${index + 1} - ${sparePartName} - ${productCode} - ${orderNumber} - ${siniestro}`;
}

export function collectLowSaleRatioViolations(
  spareParts: LowSaleRatioSparePartCandidate[],
  threshold: number = LOW_SALE_PRICE_RATIO_THRESHOLD
): LowSaleRatioViolation[] {
  const violations: LowSaleRatioViolation[] = [];
  spareParts.forEach((part, index) => {
    const ratio = getSaleToUnitRatio(part);
    if (ratio !== null && ratio <= threshold) {
      violations.push({
        index,
        ratio,
        label: formatLowSaleRatioIdentifier(part, index)
      });
    }
  });

  return violations;
}
